export type WebSearchBoost = 'normal' | 'boost' | 'superBoost';

export interface WebSearchSite {
  url: string;
  boost: WebSearchBoost;
}

export type WebSearchType = 'grounded' | 'groundedCustom';

export const roomAIAgents = {
  maya: 'maya',
  padre: 'padre',
} as const;

export type RoomAIAgent = (typeof roomAIAgents)[keyof typeof roomAIAgents];

export const roomAIChatEngines = ['v1', 'v2'] as const;

export type RoomAIChatEngine = (typeof roomAIChatEngines)[number];

export const roomAIDescriptionMaxLength = 2000;
export const roomAIRoleMaxLength = 1000;
export const roomAIVoiceCallWordMaxLength = 40;
export const roomAIDefaultBackgroundVisibilityPercent = 1;

export const isValidBackgroundVisibilityPercent = (value?: number | null) =>
  value == null || (typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 100);

export const getRoomAIBackgroundOpacity = (value?: number | null) =>
  (value != null && isValidBackgroundVisibilityPercent(value) ? value : roomAIDefaultBackgroundVisibilityPercent) / 100;

export const isValidVoiceCallWord = (value: string) =>
  value.trim().length > 0 && value.length <= roomAIVoiceCallWordMaxLength && /^[\p{L}\p{N} ]+$/u.test(value);

export interface RoomAISettings {
  isEnabled: boolean;
  voiceInputEnabled?: boolean;
  voiceCommandCallWord?: string;
  model: string | null;
  agent: RoomAIAgent;
  webSearchSites: WebSearchSite[];
  webSearchType: WebSearchType;
  webSearchInstanceName: string | null;
  displayName?: string | null;
  description?: string | null;
  roleOverride?: string | null;
  headerColor?: string | null;
  logoUrl?: string | null;
  backgroundVisibilityPercent?: number | null;
  reasoningEffort?: 'none' | 'low' | 'medium' | null;
  responseTone?: 'cold' | 'neutral' | 'warm' | null;
  search?: RoomSearchSettings | null;
  chatEngine?: RoomAIChatEngine | null;
}

export interface RoomSearchSettings {
  documentCandidateCount?: number | null;
  reciprocalRankConstant?: number | null;
  minimumDocumentScore?: number | null;
  maxChunksPerDocument?: number | null;
  collapseDuplicateDocumentsByTitle?: boolean | null;
  maxFocusedSummaryCharacters?: number | null;
  maxDocumentSources?: number | null;
  documentRelevanceFloor?: number | null;
  documentContextWindow?: number | null;
  maxSourceEvidenceCharacters?: number | null;
  maxTotalEvidenceCharacters?: number | null;
  maxWebSources?: number | null;
  maxWebCandidates?: number | null;
  webAssessmentConcurrency?: number | null;
  maxWebScanCharacters?: number | null;
}

export interface RoomAIConfiguration {
  roomId: string;
  settingsRoomId: string;
  isInherited: boolean;
  supportsRoomOverrides?: boolean;
  canInheritHubSettings?: boolean;
  settings: RoomAISettings;
  defaultRole: string;
  concurrencyToken: string;
  availableModels: string[];
  nhsHeaderColor?: string;
  canChangeChatEngine?: boolean;
  availableReasoningLevels: NonNullable<RoomAISettings['reasoningEffort']>[];
}

export type RoomAIConfigurationUpdate = Pick<RoomAIConfiguration, 'settings' | 'concurrencyToken'> & {
  useHubSettings?: boolean;
};
